/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the 'code splitting' technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */



document.addEventListener('DOMContentLoaded', function() {

  if (!!document.querySelector(".product product-form .product-info__add-button .btn") === true) {
    var textBtn = document.querySelector(".product-info__add-button .btn").textContent;
    
    // Get base prices
    var price_normal = document.querySelector(".product-info .product-info__block .product-info__price .price__current .money").textContent;
    let totalPrice = parseFloat(price_normal.replace(/[^0-9.,]/g, '').replace(',', '.')) * 100;
    let totalComparePrice = 0;

    // Get all active cross-selling products
    var activeCrossSellItems = document.querySelectorAll(".cross-selling-product-item.active");
    if (activeCrossSellItems.length > 0) {
      // Sum up prices from all active cross-sell items
      activeCrossSellItems.forEach(item => {
        let crossSellPrice = item.querySelector(".price__current .money")?.textContent || "0";
        totalPrice += parseFloat(crossSellPrice.replace(/[^0-9.,]/g, '').replace(',', '.')) * 100;
        
        let crossSellComparePrice = item.querySelector(".price__was .money")?.textContent || "0";
        totalComparePrice += parseFloat(crossSellComparePrice.replace(/[^0-9.,]/g, '').replace(',', '.')) * 100;
      });
    }

    // Format prices while preserving decimal places
    const formatPrice = (priceInCents) => {
      const price = priceInCents / 100;
      // Format to exactly 2 decimal places and replace dot with comma
      return price.toFixed(2).replace('.', ',') + ' €';
    };

    // Update button text with formatted prices
    if (!!document.querySelector(".product-info .product-info__block .product-info__price .price__was .money") === true) {
      var price_compare = document.querySelector(".product-info .product-info__block .product-info__price .price__was .money").textContent;
      totalComparePrice += Math.round(parseFloat(price_compare.replace(/[^0-9.,]/g, '').replace(',', '.')) * 100);      
      document.querySelector(".product-info__add-button .btn").innerHTML = textBtn + '<span class="priceCurrentEl">'+ formatPrice(totalPrice) +'</span><span class="priceWasEl">'+ formatPrice(totalComparePrice) +'</span>';
    } else {
      document.querySelector(".product-info__add-button .btn").innerHTML = textBtn + '<span class="priceCurrentEl">'+ formatPrice(totalPrice) +'</span>';
    }


    // Update sticky panel prices
    const stickyPriceCurrentEl = document.querySelector("sticky-atc-panel .price__current .money");
    const stickyPriceWasEl = document.querySelector("sticky-atc-panel .price__was");

    if (stickyPriceCurrentEl) {
      stickyPriceCurrentEl.textContent = formatPrice(totalPrice);
    }

    if (stickyPriceWasEl) {
      stickyPriceWasEl.innerHTML = totalComparePrice > 0 ? `<span class="money">${formatPrice(totalComparePrice)}</span>` : '';
    }
  }

  
  function checkProductInCart(handleProduct, newQtyProduct) {
    var cartDataElement = document.getElementById('cart-json');
    if (!!document.getElementById('cart-json') === true){
      var cartData = JSON.parse(cartDataElement.textContent);
    
      for (let key in cartData) {
        if (cartData[key].handle === handleProduct) {
          return {
            found: true,
            quantity: cartData[key].quantity
          };
        }
      }
  
      const newKey = `product_${Object.keys(cartData).length + 1}`;
      cartData[newKey] = {
          handle: handleProduct,
          quantity: newQtyProduct
      };
  
      cartDataElement.textContent = JSON.stringify(cartData, null, 2);
      
      return {
        found: false,
        quantity: 0,
        added: true
      };
    }
    
  }

  async function updateProductQuantity(newHandleProduct, newQtyProduct) {      
    var cartDataElement = document.getElementById('cart-json');
    if (!!document.getElementById('cart-json') === true) {
      var cartData = JSON.parse(cartDataElement.textContent);
      
      // First update the quantity
      for (let key in cartData) {
        if (cartData[key].handle === newHandleProduct) {
          cartData[key].quantity = newQtyProduct;

          // If quantity is 0, handle upsell product removal
          if (newQtyProduct === 0) {
            try {
              // Get the product ID
              const productId = cartData[key].product_id;

              // Get upsell variant IDs from localStorage
              const upsellsVariantIds = JSON.parse(localStorage.getItem(`selected_upsell_product_${productId}`)) || [];

              if (upsellsVariantIds.length > 0) {
                // Fetch current cart data
                let cartResponse = await fetch('/cart.js');
                let cartData = await cartResponse.json();

                // Remove each upsell item
                for (const variantId of upsellsVariantIds) {
                  const upsellItem = cartData.items.find(item => item.variant_id === variantId);

                  if (upsellItem) {
                    const upsellsItemElement = document.querySelector(`[data-variant-id="${variantId}"]`);
                    if (upsellsItemElement) {
                      const upsellsItemIndex = upsellsItemElement.getAttribute('data-index');
                      if (upsellsItemIndex !== null) {
                        // Remove the upsell item
                        await this.updateQuantity(upsellsItemIndex, 0);
                        // Update cart data
                        cartResponse = await fetch('/cart.js');
                        cartData = await cartResponse.json();
                      }
                    }
                  }
                }

                // Clear localStorage for this product's upsells
                localStorage.removeItem(`selected_upsell_product_${productId}`);
                window.dispatchEvent(new CustomEvent('upsell-products-removed'));
              }
            } catch (error) {
              console.error('Error removing upsell items:', error);
            }
          }

          const updatedCartJson = JSON.stringify(cartData, null, 2);
          cartDataElement.textContent = updatedCartJson;
          return {
            updated: true,
            newQuantity: newQtyProduct
          };
        }
      }
      return {
        updated: false
      };
    }
  }

  function checkQtyProduct(newQtyProduct) {
    if (!!document.querySelector(".product-info") === true){
      var handleProduct = document.querySelector(".product-info").getAttribute("product-handle");
      const result = checkProductInCart(handleProduct, newQtyProduct);
      if (result && result.found) {
        var productQuantity = parseInt(result.quantity);
        var productQuantityAuth = product_cart_max - productQuantity;
        if (!!document.querySelector(".product-info .qty-input__input") === true && productQuantityAuth > 0){
          document.querySelector(".product-info .qty-input__input").setAttribute("max", productQuantityAuth);
        }
        if (!!document.querySelector(".product-options .morphee-bundle") === true && productQuantityAuth >= 0){
          document.querySelectorAll('.product-options .morphee-bundle .morphee-bundle__choice').forEach(function(bundle) {
            var quantityBundle = parseInt(bundle.getAttribute("number"));
            if (quantityBundle > productQuantityAuth){
              bundle.classList.add("disabled");
            }else if (bundle.classList.contains("disabled") === true){
              bundle.classList.remove("disabled");
            }
          });
        }
        
        if (productQuantityAuth == 0){
          document.querySelector(".product-info__add-button .btn").setAttribute("disabled", "");
          document.querySelector("sticky-atc-panel .btn[onclick]").setAttribute("disabled", "");
        }else if (document.querySelector(".product-info__add-button .btn").hasAttribute("disabled")){
          document.querySelector(".product-info__add-button .btn").removeAttribute("disabled");
          document.querySelector("sticky-atc-panel .btn[onclick]").removeAttribute("disabled");
        }
      }
    }

    document.querySelectorAll('.btn[product-handle]').forEach(function(btnProduct) {
      var handleProduct = btnProduct.getAttribute("product-handle");
      const result = checkProductInCart(handleProduct);
      if (result && result.found) {
        var productQuantity = parseInt(result.quantity);
        if (productQuantity == product_cart_max){
          btnProduct.setAttribute("disabled", "");
        }else if (btnProduct.hasAttribute("disabled")){
          btnProduct.removeAttribute("disabled");
        }
      }
    });
  }

  const checkElements = () => {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const timeout = 2000;
      const interval = 100;
      
      const checker = setInterval(() => {
        const productInfo = document.querySelector('.product-info');
        const productButton = document.querySelector('.btn[product-handle]');
        
        if (productInfo || productButton) {
          clearInterval(checker);
          resolve({ found: true, element: productInfo || productButton });
        } else if (Date.now() - startTime >= timeout) {
          clearInterval(checker);
          resolve({ found: false, element: null });
        }
      }, interval);
    });
  };
  
  checkElements().then(result => {
    if (result && result.found) {
      checkQtyProduct();
    }
  });
  

  document.addEventListener('on:cart:add', (event) => {
    const qtyItems = document.querySelectorAll('.cart-item__qty');
    qtyItems.forEach(function(qty, index) {
      var newHandleProduct = qty.closest(".cart-item[product-handle]").getAttribute("product-handle");
      var newQtyProduct = qty.querySelector(".qty-input__input").value;
      const result = updateProductQuantity(newHandleProduct, newQtyProduct);
      if (result.updated) {
        if (index === qtyItems.length - 1) {
          setTimeout(function () {checkQtyProduct(newQtyProduct);}, 250);
        }
      }
    });
  });

  document.addEventListener('on:line-item:change', (event) => {
    const qtyItems = document.querySelectorAll('.cart-item__qty');
    qtyItems.forEach(function(qty, index) {
      var newHandleProduct = qty.closest(".cart-item[product-handle]").getAttribute("product-handle");
      var newQtyProduct = qty.querySelector(".qty-input__input").value;
      const result = updateProductQuantity(newHandleProduct, newQtyProduct);
      if (result.updated) {
        if (index === qtyItems.length - 1) {
          setTimeout(function () {checkQtyProduct(newQtyProduct);}, 250);
        }
      }
    });
  });

  document.addEventListener('on:cart-drawer:after-close', (event) => {
    const qtyItems = document.querySelectorAll('.cart-item__qty');
    qtyItems.forEach(function(qty, index) {
      var newHandleProduct = qty.closest(".cart-item[product-handle]").getAttribute("product-handle");
      var newQtyProduct = qty.querySelector(".qty-input__input").value;
      const result = updateProductQuantity(newHandleProduct, newQtyProduct);
      if (result.updated) {
        if (index === qtyItems.length - 1) {
          setTimeout(function () {checkQtyProduct(newQtyProduct);}, 250);
        }
      }
    });
  });
  


  // ====== EXTRAITS AUDIO ======
  // Délégation sur le document : fonctionne aussi pour le contenu injecté après coup (modal quick-add, pack builder...)

  const AUDIO_TILE = '.morphee-audio-tile, .card';

  function setTileState(tile, playing) {
    const playButton = tile.querySelector('.icon--play');
    const pauseButton = tile.querySelector('.icon--pause');
    if (playButton) playButton.style.display = playing ? 'none' : 'block';
    if (pauseButton) pauseButton.style.display = playing ? 'flex' : 'none';
  }

  function secureAudio(audio) {
    if (audio.dataset.secured) return;
    audio.dataset.secured = 'true';
    audio.setAttribute('crossorigin', 'anonymous');
    const source = audio.querySelector('source');
    if (source && source.getAttribute('src')) {
      try {
        const url = new URL(source.getAttribute('src').trim(), window.location.href);
        url.protocol = 'https:';
        source.src = url.toString();
        audio.load();
      } catch (e) {}
    }
  }

  function pauseAllAudios(except) {
    document.querySelectorAll(AUDIO_TILE).forEach(function(tile) {
      const audio = tile.querySelector('audio');
      if (audio && audio !== except && !audio.paused) audio.pause();
      if (audio && audio !== except) setTileState(tile, false);
    });
  }

  document.addEventListener('click', function(e) {
    const playButton = e.target.closest('.icon--play');
    const pauseButton = e.target.closest('.icon--pause');
    if (!playButton && !pauseButton) return;

    const tile = (playButton || pauseButton).closest(AUDIO_TILE);
    const audio = tile && tile.querySelector('audio');
    if (!audio) return;

    e.preventDefault();
    e.stopPropagation();

    if (playButton) {
      secureAudio(audio);
      pauseAllAudios(audio);
      audio.play().catch(function(err) { console.error('Audio :', err); });
      setTileState(tile, true);
    } else {
      audio.pause();
      setTileState(tile, false);
    }
  });

  // Fin de l'extrait (les événements média ne remontent pas : écoute en capture)
  document.addEventListener('ended', function(e) {
    if (e.target.tagName !== 'AUDIO') return;
    const tile = e.target.closest(AUDIO_TILE);
    if (tile) setTileState(tile, false);
  }, true);

  // Coupe le son à la fermeture du modal quick-add
  document.addEventListener('click', function(e) {
    if (!e.target.closest('.morphee-pc-modal-close')) return;
    document.querySelectorAll('.morphee-pc-modal-content audio').forEach(function(audio) {
      audio.pause();
      const tile = audio.closest(AUDIO_TILE);
      if (tile) setTileState(tile, false);
    });
  });


  // KGA : Gestion des envois : events audio vers GTM 09.10.24
  void 0 === window._axcb && (window._axcb = []);
  window._axcb.push(function(axeptio) {
    // Gestion du tracking uniquement si consentement
    axeptio.on("cookies:complete", function(choices) {
      if (choices.hasOwnProperty("$$googleConsentMode") && choices["$$googleConsentMode"]["ad_user_data"] === "granted") {
        initAudioTracking();
      }
    });
  });

  function initAudioTracking() {
    if (window.__audioTrackingInit) return;
    window.__audioTrackingInit = true;

    const states = new WeakMap();

    function getState(audio) {
      if (!states.has(audio)) states.set(audio, { lastProgressThreshold: 0, isPlaying: false });
      return states.get(audio);
    }

    function getAudioTitle(audio) {
      const tile = audio.closest(AUDIO_TILE);
      const titleElement = tile && tile.querySelector('.morphee-audio_tile-name p, .icons-with-text__title');
      return titleElement ? titleElement.textContent.trim() : 'Titre inconnu';
    }

    function getAudioUrl(audio) {
      const sourceElement = audio.querySelector('source');
      return sourceElement ? sourceElement.src : '';
    }

    function sendAudioEvent(audio, eventName, currentTime, duration, progressThreshold) {
      if (typeof dataLayer === 'undefined') return;
      dataLayer.push({
        event: eventName,
        audio: {
          current_time: currentTime,
          duration: duration,
          percent: progressThreshold,
          title: getAudioTitle(audio),
          url: getAudioUrl(audio)
        }
      });
    }

    function isTracked(audio) {
      return audio.tagName === 'AUDIO' && audio.closest(AUDIO_TILE);
    }

    document.addEventListener('play', function(e) {
      const audio = e.target;
      if (!isTracked(audio)) return;
      const state = getState(audio);
      state.isPlaying = true;
      const duration = audio.duration * 1000;
      const currentTime = audio.currentTime * 1000;

      if (currentTime === 0) {
        sendAudioEvent(audio, "audio_start", 0, duration, 0);
      } else {
        sendAudioEvent(audio, "audio_resume", currentTime, duration, (currentTime / duration) * 100);
      }
      state.lastProgressThreshold = 0;
    }, true);

    document.addEventListener('pause', function(e) {
      const audio = e.target;
      if (!isTracked(audio)) return;
      const state = getState(audio);
      if (state.isPlaying && !audio.ended) {
        state.isPlaying = false;
        const duration = audio.duration * 1000;
        const currentTime = audio.currentTime * 1000;
        sendAudioEvent(audio, "audio_pause", currentTime, duration, (currentTime / duration) * 100);
      }
    }, true);

    document.addEventListener('timeupdate', function(e) {
      const audio = e.target;
      if (!isTracked(audio)) return;
      const state = getState(audio);
      const currentTime = audio.currentTime * 1000;
      const duration = audio.duration * 1000;
      const progress = (currentTime / duration) * 100;

      let newThreshold = 0;
      if (progress >= 75 && state.lastProgressThreshold < 75) newThreshold = 75;
      else if (progress >= 50 && state.lastProgressThreshold < 50) newThreshold = 50;
      else if (progress >= 25 && state.lastProgressThreshold < 25) newThreshold = 25;
      else if (progress >= 10 && state.lastProgressThreshold < 10) newThreshold = 10;

      if (newThreshold > 0 && newThreshold > state.lastProgressThreshold) {
        state.lastProgressThreshold = newThreshold;
        sendAudioEvent(audio, "audio_progress", currentTime, duration, newThreshold);
      }
    }, true);

    document.addEventListener('ended', function(e) {
      const audio = e.target;
      if (!isTracked(audio)) return;
      const state = getState(audio);
      state.isPlaying = false;
      const duration = audio.duration * 1000;
      sendAudioEvent(audio, "audio_complete", duration, duration, 100);
    }, true);
  }

});


// Vidéo display
window.addEventListener("load", (event) => {
  setTimeout(function () {    
    if (!!document.querySelector(".multi-column video-component .icon--pause+video") === true) {
      var videos = document.querySelectorAll('.multi-column video-component .icon--pause+video');
      for (var i = 0; i < videos.length; i++) {
        videos[i].addEventListener('pause', function(event) {
          event.currentTarget.parentElement.querySelector(".icon--play").style.display = "block";
          event.currentTarget.parentElement.querySelector(".icon--pause").style.display = "none";
        });
        
        videos[i].addEventListener('play', function(event) {
          if (event.currentTarget.muted) {
            event.currentTarget.muted = false;
          }
          event.currentTarget.parentElement.querySelector(".icon--play").style.display = "none";
          event.currentTarget.parentElement.querySelector(".icon--pause").style.display = "block";
        });
      }
    }
  }, 1000);
});


/* ====== Detect Horizontal Overflow ====== */

document.querySelectorAll('.tabs-section__arrows .arrow').forEach(arrow => {
  arrow.addEventListener('click', function() {
    const arrowElement = this;
    
    setTimeout(() => {
      const scrollContainer = document.querySelector('.tabs-section__container');
      const leftScrolledVal = scrollContainer.scrollLeft;
      const containerWidth = scrollContainer.offsetWidth;
      const containerScrollWidth = scrollContainer.scrollWidth;
      const actualScrollWidth = containerScrollWidth - containerWidth;
      const scrolledPercentage = Math.round((leftScrolledVal / actualScrollWidth) * 100);

      if (scrolledPercentage === 0 && arrowElement.classList.contains('prev-arrow')) {
        arrowElement.classList.add('hide');
        arrowElement.nextElementSibling.classList.remove('hide');
      }

      if (scrolledPercentage > 0 && arrowElement.classList.contains('next-arrow')) {
        arrowElement.previousElementSibling.classList.remove('hide');
        if (scrolledPercentage === 100) {
          arrowElement.classList.add('hide');
        }
      }
    }, 300);
  });
});

function tabsItemsContainerArrows() {
  const documentWidth = window.innerWidth;
  const tabsContainer = document.querySelector('.tabs-section__container');
  
  if (!tabsContainer) return;
  
  const tabsScrollWidth = tabsContainer.scrollWidth;
  const arrows = document.querySelectorAll('.tabs-section__arrows .arrow');
  
  if (tabsScrollWidth > documentWidth) {
    arrows.forEach(arrow => arrow.classList.remove('hide'));
  } else {
    arrows.forEach(arrow => arrow.classList.add('hide'));
  }
}

const tabsContainer = document.querySelector('.tabs-section__container');
if (tabsContainer) {
  if (window.getComputedStyle(tabsContainer).display !== 'none') {
    tabsItemsContainerArrows();
  }
  
  window.addEventListener('resize', tabsItemsContainerArrows);
}



/* NOUVEAU MEGA MENU JAN 2026 */

document.addEventListener("DOMContentLoaded", function() {
  var nouveau_mega_menu = document.querySelector(".nouveau-mega-menu.main-nav__child");
  if (nouveau_mega_menu){
    nouveau_mega_menu.querySelectorAll(".titre-categorie").forEach(function(titre) {
      titre.addEventListener("mouseenter", function() {
        nouveau_mega_menu.querySelector(".titre-categorie.active")?.classList.remove("active");
        this.classList.add("active");
        
        var selectValue = this.getAttribute("select");
        nouveau_mega_menu.querySelector(".details-categories .categorie.active")?.classList.remove("active");
        var newCategorie = nouveau_mega_menu.querySelector('.details-categories .categorie[select="' + selectValue + '"]');
        newCategorie?.classList.add("active");
        
        // Réinitialiser le premier li et le premier produit comme actifs
        newCategorie?.querySelector(".sous-menu li.active")?.classList.remove("active");
        newCategorie?.querySelector(".sous-menu li")?.classList.add("active");
        
        newCategorie?.querySelector(".liste-produits-sous-menu .produit-sous-menu.active")?.classList.remove("active");
        newCategorie?.querySelector(".liste-produits-sous-menu .produit-sous-menu")?.classList.add("active");
      });
    });

    nouveau_mega_menu.querySelectorAll(".details-categories .sous-menu li").forEach(function(li) {
      li.addEventListener("mouseenter", function() {
        var categorieActive = this.closest(".categorie");
        
        categorieActive?.querySelector(".sous-menu li.active")?.classList.remove("active");
        this.classList.add("active");
        
        var selectValue = this.getAttribute("select");
        categorieActive?.querySelector(".liste-produits-sous-menu .produit-sous-menu.active")?.classList.remove("active");
        categorieActive?.querySelector('.liste-produits-sous-menu .produit-sous-menu[select="' + selectValue + '"]')?.classList.add("active");
      });
    });
  }
});